/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {BaseComponent, Dialog_Builder, TS_Input} from "@intuitionrobotics/thunderstorm/frontend";
import {DialogCard} from "../../widget/DialogCard";
import {
    CareFieldDef,
    CareFieldVal,
    Frequency,
    getRangeByTimeUnitForKey,
    Key_After_Breakfast,
    Key_After_Dinner,
    Key_After_Lunch,
    Key_Before_Breakfast,
    Key_Before_Dinner,
    Key_Before_Lunch,
    MeasurementRange,
    measurementsElements,
    SubKey,
    SubKeys
} from "@app/app-shared/care_config";
import {FONTS} from "@res/fonts";
import {COLORS} from "@res/colors";
import {Checkbox} from "./Checkbox";
import {capitalize} from "@app/ir-q-app-common/types/care-center/user";
import {_keys, BadImplementationException} from "@intuitionrobotics/ts-common";
import {ErrorToast} from "../../../themes/toasts";
import {FrequencySelector} from "../FrequencySelector";

type Props = {
    definition: CareFieldDef;
    value: CareFieldVal;
    presentation: string,
    onCareConfigUpdate: (val: CareFieldVal) => void,
    on?: boolean,
    notCustomEndpoint?: boolean
}

type State = {
    firstEdit: boolean
    ranges?: {
        [key: string]: {
            min?: number;
            max?: number;
        } | undefined;
    },
    frequency: Frequency,
    ttl?: number,
    enableNotification: boolean,
    on?: boolean
}

export class ConfigRangeDialog
    extends BaseComponent<Props, State> {

    constructor(props: Props) {
        super(props);
        this.state = {
            firstEdit: false,
            ranges: this.getSubKeysDefaultRanges(),
            frequency: {...this.props.value.frequency},
            ttl: this.props.value.ttl,
            enableNotification: !!this.props.value.ranges && Object.keys(this.props.value.ranges).length > 0,
            on: !!this.props.value.ttl
        };
    }

    public static show(config: CareFieldDef, presentation: string, value: CareFieldVal, onCareConfigUpdate: (val: CareFieldVal) => void, notCustomEndpoint = false) {
        new Dialog_Builder(
            <ConfigRangeDialog definition={config} presentation={presentation} value={value} onCareConfigUpdate={onCareConfigUpdate}
                               notCustomEndpoint={notCustomEndpoint}/>
        ).setAllowIndirectClosing(true)
            .show();
    }

    private renderCustomRecurrence = () => {
        const range = (!measurementsElements.includes(this.props.definition.key)) ? {
            start: 1,
            end: 2
        } : getRangeByTimeUnitForKey(this.state.frequency.value,
            this.props.definition.key);

        return <div>
            <div style={{margin: "16px"}}>
                {FONTS.semi_bold_14("Custom recurrence")}
            </div>
            <FrequencySelector
                label={(this.state.frequency.times === 1) ? "time" : "times"}
                frequency={this.state.frequency}
                range={range}
                timeUnits={["day",
                    "week",
                    "month"]}
                onFrequencyChange={(frequency) => {
                    this.setState({frequency})
                }}/>
        </div>;
    };

    renderAlertOptIn = () => {
        return <>{FONTS.medium_16_blue("Healthy range", {marginTop: "16px"})}
            <div className={"ll_h_c"} style={{margin: "16px"}}>
                <div style={{display: "flex", alignItems: "center"}}>
                    <Checkbox
                        on={this.state.enableNotification}
                        onChange={() => this.setState(s => {
                            return ({
                                enableNotification: !s.enableNotification,
                                ranges: s.enableNotification ? {} : this.getSubKeysDefaultRanges()
                            });
                        })}/>
                    <div style={{marginLeft: "8px"}}>
                        {FONTS.semi_bold_14("Notify me if measurements are outside these ranges:", {color: COLORS.neutral_text_dark})}
                    </div>
                </div>
            </div>
        </>;
    };

    private getSubKeysDef = () => {
        const instance = this.props.definition.subKeys;
        if (!instance)
            throw new BadImplementationException(`Missing prop definition `);

        return instance;
    };

    private renderRange = (label: string, instanceElement: SubKey) => {
        const validRange = instanceElement.defaultRange;

        let rangeLabel = label;

        if (rangeLabel === Key_Before_Breakfast)
            rangeLabel = "Fasting (Before breakfast)";
        else if (rangeLabel === Key_Before_Lunch)
            rangeLabel = "Before meal";
        else if (rangeLabel === Key_After_Lunch)
            rangeLabel = "After meal";

        return <React.Fragment key={label}>
            <div style={{margin: 16}}>
                {FONTS.reg_14(`${capitalize(rangeLabel)} range (${instanceElement.measurement})`,
                    {color: this.state.enableNotification ? COLORS.neutral_text_dark : "rgb(236, 236, 238)"})}
            </div>
            <div style={!this.state.enableNotification ? {pointerEvents: "none", opacity: "0.4", display: "flex"} : {display: "flex"}}>
                <TS_Input
                    focus={!this.state.firstEdit && this.props.notCustomEndpoint && this.state.enableNotification && label !== Key_Before_Breakfast && label !== "diastolic"}
                    value={this.state.ranges?.[label]?.min?.toString() || validRange?.min.toString()}
                    onChange={(min: string) => {
                        const intMin = Number(min);
                        this.setState(state => ({
                                ...state,
                                firstEdit: true,
                                ranges: {
                                    ...state.ranges,
                                    [label]: {
                                        ...state.ranges?.[label],
                                        min: intMin
                                    }
                                }
                            })
                        );
                    }}
                    type={"number"}
                    style={{border: "solid", borderColor: "#ececee", margin: 16, marginTop: 8, marginBottom: 8, borderRadius: 1, width: 50}}
                    id={label + "-min"}/>
                {FONTS.reg_14("to", {color: COLORS.neutral_text_dark, margin: 16})}
                <TS_Input
                    value={this.state.ranges?.[label]?.max?.toString() || validRange?.max.toString()}
                    onChange={(max: string) => {
                        const intMax = Number(max);
                        this.setState(state => ({
                            ...state,
                            firstEdit: true,
                            ranges: {
                                ...state.ranges,
                                [label]: {
                                    ...state.ranges?.[label],
                                    max: intMax
                                }
                            }
                        }));
                    }}
                    type={"number"}
                    style={{border: "solid", borderColor: "#ececee", margin: 16, marginTop: 8, marginBottom: 8, borderRadius: 1, width: 50}}
                    id={label + "-max"}/>
            </div>
        </React.Fragment>;
    };

    private separator = () => <div style={{height: "1px", width: "100%", backgroundColor: "#ececee"}}/>;

    onSave = (): true | void => {
        const ranges = this.state.ranges;

        const {value, onCareConfigUpdate} = this.props;
        const newValue = JSON.parse(JSON.stringify(value));
        if (this.state.enableNotification && ranges && Object.keys(ranges).length !== 0) {

            const realRanges: MeasurementRange = {};
            const keys = _keys(ranges);

            if (keys.every(
                    k => ranges[k]?.min === newValue.ranges?.[k]?.min && ranges[k]?.max === newValue.ranges?.[k]?.max) && this.state.ttl === newValue.ttl &&
                this.state.frequency.times === newValue.frequency.times && this.state.frequency.value === newValue.frequency.value && this.state.frequency.type === newValue.frequency.type) {
                return true;
            }

            for (const r of keys) {

                if ([Key_Before_Dinner,
                    Key_After_Dinner,
                    Key_After_Breakfast].includes(r as string))
                    continue;

                const min = ranges[r]?.min;
                if (!min)
                    return ErrorToast(`Please provide a min value for the measure ${r}`);

                const max = ranges[r]?.max;
                if (!max)
                    return ErrorToast(`Please provide a max value for the measure ${r}`);

                if (max <= 0 || min <= 0)
                    return ErrorToast(`Please input a valid range: you selected a non-positive value for ${r}`);

                if (min > max)
                    return ErrorToast(`Please provide a max value bigger then the min value for the measure ${r}`);

                if (min > 999 || max > 999)
                    return ErrorToast(`Please provide a value less than 999 for the measure ${r}`);

                realRanges[r] = {min, max};

                if (r === Key_Before_Lunch)
                    realRanges[Key_Before_Dinner] = {min, max};
                else if (r === Key_After_Lunch) {
                    realRanges[Key_After_Breakfast] = {min, max};
                    realRanges[Key_After_Dinner] = {min, max};
                }
            }
            newValue.ranges = realRanges;

        } else {
            newValue.ranges = undefined;
        }

        if (this.state.frequency.times === 0)
            return ErrorToast(`Please input a valid frequency: you selected 0 times a ${this.state.frequency.type}`);

        newValue.frequency = this.state.frequency;
        newValue.ttl = this.state.ttl;
        onCareConfigUpdate(newValue);
        return true;
    };

    private renderContent = () => {
        return <div>
            {this.renderCustomRecurrence()}
            {/*{this.renderTTL()}*/}
            {this.separator()}
            {this.renderAlertOptIn()}
            {this.state.enableNotification && this.getSubKeys()}
        </div>;
    };

    private getSubKeysDefaultRanges = (): MeasurementRange => {
        const ranges: MeasurementRange = {};
        const instance = this.props.definition.subKeys;
        if (instance) {
            _keys(instance).forEach(_key => {
                instance[_key].defaultRange
                const defaultRange = instance[_key]?.defaultRange;
                return ranges[_key] = {
                    min: this.props.value.ranges?.[_key]?.min || defaultRange?.min || 0,
                    max: this.props.value.ranges?.[_key]?.max || defaultRange?.max || 0
                };
            });
        }
        return ranges;
    };

    private getSubKeys = () => {
        const instance = this.getSubKeysDef();
        return _keys<SubKeys, string>(instance).filter(_key => ![Key_Before_Dinner,
            Key_After_Breakfast,
            Key_After_Dinner].includes(_key))
            .map(_key => {
                return this.renderRange(_key, instance[_key]);
            });
    };

    render() {
        return <DialogCard
            title={`${this.props.presentation} | Advanced Settings`}
            content={this.renderContent()}
            saveEnabled={this.state.frequency.times !== 0}
            onSave={() => !this.onSave()}
            style={{color: "#026691"}}
        />;

    }

}
